// Persistent player roster, kept independent of round data.
//
// The roster is the user's address book of regulars; each entry has a
// stable id so the in-round PlayersModal can match a free-text name
// (or a datalist pick) against an existing roster record and pull the
// handicap forward into the round.
//
// Roster lives in localStorage under one JSON-encoded key (read hot
// path stays sync for the rest of the module) AND syncs to Cosmos via
// caddieGamesStore.queueBackendPush — that's the side that survives
// fresh installs / logout-relogin. Rounds copy roster data into
// round.players at start time — once a round is live it MUST NOT write
// back here, so editing a player during scoring never silently mutates
// the roster.

import { queueBackendPush } from "./caddieGamesStore";

export interface RosterPlayer {
  id: string;
  name: string;
  handicapIndex?: number;
  // Set once a roster entry has been linked to a real CaddieGames user via
  // the Connect flow. linkedUserId is the canonical link; the cached
  // linkedName / linkedHomeCourse are display conveniences so a connected
  // row can render "Name (Home Course)" + the ✔ Connected state without a
  // round-trip. Refreshed whenever the user re-connects.
  linkedUserId?: string;
  linkedName?: string;
  linkedHomeCourse?: string;
  // Past names this player has been known by — captured automatically when a
  // player is added via handicap import and whenever the user renames them.
  // The import matcher checks these so shortening "RICHARD HORODYSKI" to "Rick"
  // doesn't break the next import (it still matches the alias and only updates
  // the handicap). Hidden from the UI.
  aliases?: string[];
}

// Owner = the app user themselves. A single record (separate from the
// general roster) so we can pin them at the top of the Players page and
// surface them in the in-round name picker as "you" without conflating
// with regular roster entries.
export interface RosterOwner {
  name: string;
  handicapIndex?: number;
  // The user's home club. Set on the profile (owner) card and published
  // to the Connect directory so others can identify them across clubs
  // (e.g. "Chris Dopp (Blue Mountain)"). Stored as the plain course name.
  homeCourse?: string;
  // Preferred currency symbol used by every betting-output surface
  // (Leaderboard sub-boards). Stored as the literal symbol so we can
  // concatenate it without a lookup. Defaults to "$" when unset.
  betCurrency?: string;
}

const STORAGE_KEY = "asksmart.caddiegames.roster";
const OWNER_KEY = "asksmart.caddiegames.owner";
const ID_PREFIX = "rp_";
// Sentinel id used when the owner is included in the in-round
// "all known players" list. Doesn't collide with rp_… roster ids.
export const OWNER_ID = "owner";

// Default Home Course for a player not attached to a club. Home Course is how
// players are identified/searched in Connect, so it must never be blank — the
// profile field pre-fills with this, restores it if cleared, and any legacy
// record saved blank is shown as this via homeCourseLabel().
export const PUBLIC_PLAYER = "Public Player";

// Display/behaviour label for a home course: the trimmed value, or
// "Public Player" when blank/unset. Use everywhere a home course is rendered
// so existing blank records read consistently.
export function homeCourseLabel(value?: string | null): string {
  const v = (value || "").trim();
  return v || PUBLIC_PLAYER;
}

export function loadRoster(): RosterPlayer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(list: RosterPlayer[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // private mode / quota — best effort
  }
  queueBackendPush();
}

export function addRosterPlayer(
  p: Omit<RosterPlayer, "id">,
): RosterPlayer {
  const record: RosterPlayer = {
    id: `${ID_PREFIX}${Math.random().toString(36).slice(2, 10)}`,
    ...p,
  };
  const list = loadRoster();
  list.push(record);
  persist(list);
  return record;
}

export function updateRosterPlayer(
  id: string,
  patch: Partial<Omit<RosterPlayer, "id">>,
): void {
  const list = loadRoster().map((p) => {
    if (p.id !== id) return p;
    const next = { ...p, ...patch };
    // On a rename, remember the old name as an alias so an import using the
    // old (e.g. full Golf Canada) name still matches this player.
    if (typeof patch.name === "string") {
      const oldName = p.name.trim();
      const newName = patch.name.trim();
      if (oldName && oldName.toLowerCase() !== newName.toLowerCase()) {
        next.aliases = withAlias(p.aliases, oldName, newName);
      }
    }
    return next;
  });
  persist(list);
}

// Append `alias` to an alias list, deduped case-insensitively and skipping
// anything equal to the current name. Returns a new array (or the original
// when nothing changed).
function withAlias(
  aliases: string[] | undefined,
  alias: string,
  currentName: string,
): string[] {
  const a = alias.trim();
  const lower = a.toLowerCase();
  const list = aliases || [];
  if (!a || lower === currentName.trim().toLowerCase()) return list;
  if (list.some((x) => x.trim().toLowerCase() === lower)) return list;
  return [...list, a];
}

// Add an alias to an existing roster player (used by the import flow when the
// user links an extracted name to a player it didn't auto-match). No-op when
// the alias equals the current name or is already present.
export function addRosterAlias(id: string, alias: string): void {
  let changed = false;
  const list = loadRoster().map((p) => {
    if (p.id !== id) return p;
    const next = withAlias(p.aliases, alias, p.name);
    if (next === p.aliases) return p;
    changed = true;
    return { ...p, aliases: next };
  });
  if (changed) persist(list);
}

export function deleteRosterPlayer(id: string): void {
  persist(loadRoster().filter((p) => p.id !== id));
}

// Case-insensitive exact-name lookup. Used by the in-round PlayersModal
// to detect when a typed/picked name matches an existing roster entry
// so the handicap can come along for the ride.
export function findRosterByName(name: string): RosterPlayer | undefined {
  const target = name.trim().toLowerCase();
  if (!target) return undefined;
  return loadRoster().find((p) => p.name.toLowerCase() === target);
}

// Reconcile the server's connection list into the local roster so both
// sides of a mutual link stay in sync. First, UNLINK any row whose connection
// has vanished (the other user disconnected or deleted their account) — the
// player stays as a plain roster row, just without the link/Connected state,
// so a captain can see a teammate drop off and nudge them to (re-)sign up.
// Then, for each current connection:
//   • already linked (linkedUserId match) → refresh cached display fields
//   • an unlinked roster row with the same name → adopt the link (MERGE,
//     never a duplicate)
//   • otherwise → add a new Connected roster row
// Connections whose user hasn't set a profile name yet are skipped (nothing
// to show until they do). Returns true when the roster changed.
//
// MUST be called with a DEFINITIVE connection list (never on a failed fetch),
// or the unlink step would wrongly drop still-valid links — fetchConnections
// returns null on failure precisely so the caller can guard this.
//
// Future limit hook: when a roster cap is introduced this is where a brand
// new add (the last branch) would be blocked or routed to the
// "remove an existing player" prompt instead of pushed.
export interface ConnectionInput {
  userId: string;
  name: string;
  homeCourse?: string | null;
  handicapIndex?: number | null;
}

export function applyConnections(connections: ConnectionInput[]): boolean {
  const list = loadRoster();
  let changed = false;

  // Unlink rows whose connection no longer exists. Keeps the player, drops
  // only the link fields + Connected badge.
  const connectedIds = new Set(connections.map((c) => c.userId));
  for (const p of list) {
    if (p.linkedUserId && !connectedIds.has(p.linkedUserId)) {
      delete p.linkedUserId;
      delete p.linkedName;
      delete p.linkedHomeCourse;
      changed = true;
    }
  }

  for (const conn of connections) {
    const name = (conn.name || "").trim();
    if (!name) continue;
    const linkedHomeCourse = conn.homeCourse || undefined;
    // Pull the connected user's handicap from their profile, but never clear
    // an existing one when their profile has none set.
    const connHcp =
      typeof conn.handicapIndex === "number" ? conn.handicapIndex : undefined;

    const existingLinked = list.find((p) => p.linkedUserId === conn.userId);
    if (existingLinked) {
      if (
        existingLinked.linkedName !== name ||
        existingLinked.linkedHomeCourse !== linkedHomeCourse ||
        (connHcp !== undefined && existingLinked.handicapIndex !== connHcp)
      ) {
        existingLinked.linkedName = name;
        existingLinked.linkedHomeCourse = linkedHomeCourse;
        if (connHcp !== undefined) existingLinked.handicapIndex = connHcp;
        changed = true;
      }
      continue;
    }

    const byName = list.find(
      (p) =>
        !p.linkedUserId && p.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (byName) {
      byName.linkedUserId = conn.userId;
      byName.linkedName = name;
      byName.linkedHomeCourse = linkedHomeCourse;
      if (connHcp !== undefined) byName.handicapIndex = connHcp;
      changed = true;
      continue;
    }

    list.push({
      id: `${ID_PREFIX}${Math.random().toString(36).slice(2, 10)}`,
      name,
      handicapIndex:
        typeof conn.handicapIndex === "number" ? conn.handicapIndex : undefined,
      linkedUserId: conn.userId,
      linkedName: name,
      linkedHomeCourse,
    });
    changed = true;
  }
  if (changed) persist(list);
  return changed;
}

// Connected linkedUserIds for the given round players — matched by name
// (round players are seeded from roster/owner names). Used at end-round to
// fan the round out to the connected participants' histories.
export function connectedTargetsForNames(names: string[]): string[] {
  const roster = loadRoster();
  const targets = new Set<string>();
  for (const raw of names) {
    const name = raw.trim().toLowerCase();
    if (!name) continue;
    const match = roster.find(
      (p) => p.linkedUserId && p.name.trim().toLowerCase() === name,
    );
    if (match?.linkedUserId) targets.add(match.linkedUserId);
  }
  return Array.from(targets);
}

// ---------- owner ----------

export function loadOwner(): RosterOwner | null {
  try {
    const raw = localStorage.getItem(OWNER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.name === "string") return parsed;
    return null;
  } catch {
    return null;
  }
}

export function saveOwner(owner: RosterOwner): void {
  try {
    localStorage.setItem(OWNER_KEY, JSON.stringify(owner));
  } catch {
    // private mode / quota — best effort
  }
  queueBackendPush();
}
